import { useEffect, useRef } from 'react';

import { MfaMethod } from '@/api/mfa/types';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import {
  BottomSheet,
  Button,
  Modal,
  OtpInput,
  type OtpInputHandle,
  Spinner,
  theme,
  Typography,
} from '@/shared/design-system';

import {
  MethodActions,
  OtpBlock,
  PendingStatus,
  VerificationContent,
} from './mfa-verification.styled';
import { type MfaVerificationController, MfaVerificationPhase } from './use-mfa-verification';

const MethodChooser = ({
  controller,
  description,
}: {
  controller: MfaVerificationController;
  description: string;
}) => (
  <VerificationContent className="ph-no-capture">
    <Typography variant="body3" color={theme.colors.text.secondary}>
      {description}
    </Typography>
    <MethodActions>
      {controller.state.request?.allowedMethods.includes(MfaMethod.Totp) && (
        <Button fullWidth variant="outline" onClick={() => controller.chooseMethod(MfaMethod.Totp)}>
          Use authenticator app
        </Button>
      )}
      {controller.state.request?.allowedMethods.includes(MfaMethod.Passkey) && (
        <Button
          fullWidth
          variant="outline"
          onClick={() => controller.chooseMethod(MfaMethod.Passkey)}
        >
          Use passkey
        </Button>
      )}
    </MethodActions>
  </VerificationContent>
);

const TotpChallenge = ({ controller }: { controller: MfaVerificationController }) => {
  const otpRef = useRef<OtpInputHandle>(null);
  const { state } = controller;

  useEffect(() => {
    if (state.error) {
      otpRef.current?.focusFirst();
    }
  }, [state.error]);

  return (
    <VerificationContent className="ph-no-capture">
      <Typography variant="body2" color={theme.colors.text.secondary}>
        Enter the six-digit code from your authenticator app.
      </Typography>
      <OtpBlock>
        <OtpInput
          ref={otpRef}
          length={6}
          groupSize={3}
          value={state.code}
          onChange={controller.setCode}
          error={Boolean(state.error)}
        />
        {state.error && (
          <Typography role="alert" variant="body3" color={theme.colors.text.error}>
            {state.error}
          </Typography>
        )}
      </OtpBlock>
    </VerificationContent>
  );
};

const PendingChallenge = () => (
  <PendingStatus className="ph-no-capture" role="status" aria-live="polite" aria-busy="true">
    <Spinner />
    <Typography variant="body3" color="inherit">
      Confirming your identity…
    </Typography>
  </PendingStatus>
);

const VerificationFailure = ({ controller }: { controller: MfaVerificationController }) => (
  <VerificationContent className="ph-no-capture">
    <Typography role="alert" variant="body3" color={theme.colors.text.error}>
      {controller.state.error}
    </Typography>
    <Button variant="outline" onClick={controller.retry}>
      {controller.state.retryMethod ? 'Retry' : 'Close'}
    </Button>
  </VerificationContent>
);

const VerificationBody = ({
  controller,
  chooserDescription,
}: {
  controller: MfaVerificationController;
  chooserDescription: string;
}) => {
  if (controller.state.phase === MfaVerificationPhase.Choose) {
    return <MethodChooser controller={controller} description={chooserDescription} />;
  }
  if (controller.state.phase === MfaVerificationPhase.Totp) {
    return <TotpChallenge controller={controller} />;
  }
  if (controller.state.phase === MfaVerificationPhase.Pending) {
    return <PendingChallenge />;
  }
  return <VerificationFailure controller={controller} />;
};

export const MfaVerificationDialog = ({
  controller,
  title = 'Confirm security change',
  chooserDescription = 'Choose how you want to confirm this security change.',
}: {
  controller: MfaVerificationController;
  title?: string;
  chooserDescription?: string;
}) => {
  const isMobile = useIsMobile();
  const content = (
    <VerificationBody controller={controller} chooserDescription={chooserDescription} />
  );

  const open = controller.state.phase !== MfaVerificationPhase.Closed;
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      controller.cancel();
    }
  };

  if (!isMobile) {
    return (
      <Modal
        open={open}
        onOpenChange={handleOpenChange}
        title={title}
        maxWidth={480}
        gap={theme.spacing[24]}
      >
        {content}
      </Modal>
    );
  }
  return (
    <BottomSheet
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      maxHeight={`calc(100dvh - ${theme.spacing[56]})`}
    >
      {content}
    </BottomSheet>
  );
};
